import ISystemStatusMessage, {
  DerivedStatus,
  Severity
} from '../model/ISystemStatusMessage'

type Scheduled = Pick<ISystemStatusMessage, 'startDate' | 'expiringDate'>
type Content = Pick<
  ISystemStatusMessage,
  'type' | 'title' | 'body' | 'startDate' | 'expiringDate'
>

// Single source of truth for the lifecycle status, shared by the banner and
// the BackOffice table. Boundaries: at exactly startDate the message is Active;
// at exactly expiringDate it is Expired ([start, expiry) is the Active window).
export const deriveStatus = (
  msg: Scheduled,
  now: number = Date.now()
): DerivedStatus => {
  const start = new Date(msg.startDate).getTime()
  const expiry = new Date(msg.expiringDate).getTime()
  if (now < start) {
    return 'Pending'
  }
  if (now < expiry) {
    return 'Active'
  }
  return 'Expired'
}

export const isActive = (msg: Scheduled, now: number = Date.now()): boolean =>
  deriveStatus(msg, now) === 'Active'

// Ordering used by BOTH surfaces: severity first (Critical → Warning →
// Informational, i.e. ascending enum value), then startDate ascending
// (oldest first) within a severity. Does not mutate the input.
export const sortMessages = <T extends { type: Severity; startDate: string }>(
  messages: T[]
): T[] =>
  [...messages].sort((a, b) => {
    if (a.type !== b.type) {
      return a.type - b.type
    }
    return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
  })

const SEVERITY_RANK: Record<Severity, number> = {
  [Severity.Critical]: 3,
  [Severity.Warning]: 2,
  [Severity.Informational]: 1
}

const STATUS_RANK: Record<DerivedStatus, number> = {
  Active: 3,
  Pending: 2,
  Expired: 1
}

export type StatusMessageSortField =
  | 'type'
  | 'title'
  | 'status'
  | 'startDate'
  | 'expiringDate'

export const statusMessageSortKey = (
  field: StatusMessageSortField,
  now: number = Date.now()
): ((msg: ISystemStatusMessage) => number | string) => {
  switch (field) {
    case 'title':
      return msg => msg.title.toLowerCase()
    case 'status':
      return msg => STATUS_RANK[deriveStatus(msg, now)]
    case 'startDate':
      return msg => new Date(msg.startDate).getTime()
    case 'expiringDate':
      return msg => new Date(msg.expiringDate).getTime()
    case 'type':
    default:
      return msg => SEVERITY_RANK[msg.type] ?? 0
  }
}

// Active messages, severity-sorted — what the banner renders.
export const getActiveMessages = (
  messages: ISystemStatusMessage[],
  now: number = Date.now()
): ISystemStatusMessage[] =>
  sortMessages(messages.filter(msg => isActive(msg, now)))

// The backend may serialise MessageType either as an int (10/20/30) or as its
// name ("Critical"/"Warning"/"Informational"). Normalise to the numeric enum so
// the whole app can rely on one representation. Unknown values fall back to
// Informational rather than crashing a render.
const SEVERITY_BY_NAME: Record<string, Severity> = {
  critical: Severity.Critical,
  warning: Severity.Warning,
  informational: Severity.Informational,
  info: Severity.Informational
}

export const normalizeSeverity = (raw: unknown): Severity => {
  if (typeof raw === 'number' && raw in Severity) {
    return raw as Severity
  }
  if (typeof raw === 'string') {
    const byName = SEVERITY_BY_NAME[raw.trim().toLowerCase()]
    if (byName !== undefined) {
      return byName
    }
    const n = Number(raw)
    if (!Number.isNaN(n) && n in Severity) {
      return n as Severity
    }
  }
  return Severity.Informational
}

export const normalizeMessage = (
  msg: ISystemStatusMessage
): ISystemStatusMessage => ({
  ...msg,
  type: normalizeSeverity(msg.type)
})

// Stable content fingerprint used as the dismissal key. Because the backend
// exposes no updatedUtc, we hash the fields an admin can edit: any edit changes
// the hash, so a previously dismissed message resurfaces. djb2 (xor variant).
export const messageContentHash = (msg: Content): string => {
  const raw = `${msg.type}|${msg.title}|${msg.body}|${msg.startDate}|${msg.expiringDate}`
  let hash = 5381
  for (let i = 0; i < raw.length; i++) {
    hash = (hash * 33) ^ raw.charCodeAt(i)
  }
  return (hash >>> 0).toString(36)
}
